import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * A read value with a "verified" icon after it (the BAG check on an address, the KvK check on a
 * company number). The projected content is the value itself; the icon only renders when
 * `verified` is true, otherwise the content is rendered on its own, exactly as without this
 * wrapper.
 *
 * The verified branch wraps text + icon in its own `inline-flex` box instead of leaving the icon
 * as trailing inline content: the row's value span (`label-list.component.ts`) applies
 * `overflow-clip` + `text-ellipsis` + `whitespace-nowrap` for a single-line row, and this
 * component's own host is `display: contents` — so without the wrapper, a long value that fills
 * the row clips the trailing icon along with the overflow text. `max-w-full` on the wrapper caps
 * it at the row's own width; `min-w-0` + `truncate` on the text span let IT shrink and ellipsize
 * while the `flex-none` icon keeps its full size and stays inside the row.
 */
@Component({
  selector: 'dd-verified-value',
  imports: [MatIconModule, MatTooltipModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — same reason as ValueDisplayComponent: no box inside the value span.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    <!-- Declared once and stamped into whichever branch renders: a second <ng-content> would not project. -->
    <ng-template #content><ng-content /></ng-template>
    @if (verified()) {
      <span class="inline-flex max-w-full items-center gap-1.5 align-bottom">
        <span class="min-w-0 truncate">
          <ng-container [ngTemplateOutlet]="content" />
        </span>
        <mat-icon
          class="flex-none align-middle icon-size-4 text-primary"
          role="img"
          aria-hidden="false"
          [attr.aria-label]="label()"
          [matTooltip]="label()"
          [attr.data-cy]="iconDataCy()"
          >{{ icon() }}</mat-icon
        >
      </span>
    } @else {
      <ng-container [ngTemplateOutlet]="content" />
    }
  `,
})
export class VerifiedValueComponent {
  readonly verified = input.required<boolean>();
  /** Material icon ligature shown after the value. */
  readonly icon = input.required<string>();
  /** Accessible name and tooltip of the icon, e.g. "Geverifieerd via BAG". */
  readonly label = input.required<string>();
  /** `data-cy` of the icon, so e2e and specs can tell the checks apart. */
  readonly iconDataCy = input<string>();
}
